import { useState } from 'react';
import {
  LineChart, Line, BarChart, Bar,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, Cell,
} from 'recharts';

/* ── Currency formatter ─────────────────────────────────────── */
const fmt = (n: number) =>
  n.toLocaleString('es-MX', { style: 'currency', currency: 'MXN', maximumFractionDigits: 0 });

/* ════════════════════════════════════════════════════════════════
   SECTION 1 — INSURANCE PROJECTION
   ════════════════════════════════════════════════════════════════ */
interface SimInput {
  tipoSeguro: string; aseguradora: string; nombreCliente: string;
  edadAsegurado: number; primaMensual: number; sumaAsegurada: number;
  plazo: number; tasaAnual: number;
}
interface YearRow {
  anio: number; edad: number; totalPagado: number;
  valorRescate: number; intereses: number; sumaAsegurada: number;
}

function calcularProyeccion(input: SimInput): YearRow[] {
  const { primaMensual, sumaAsegurada, plazo, tasaAnual, edadAsegurado } = input;
  const r = tasaAnual / 100 / 12;
  return Array.from({ length: plazo }, (_, i) => {
    const n = i + 1;
    const months = n * 12;
    const totalPagado = primaMensual * months;
    const valorRescate = r > 0
      ? primaMensual * ((Math.pow(1 + r, months) - 1) / r)
      : totalPagado;
    return {
      anio: n, edad: edadAsegurado + n,
      totalPagado: Math.round(totalPagado),
      valorRescate: Math.round(valorRescate),
      intereses: Math.round(Math.max(0, valorRescate - totalPagado)),
      sumaAsegurada,
    };
  });
}

const ChartTooltip = ({ active, payload, label }: any) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-white rounded-xl shadow-lg border border-gray-100 p-3 text-xs">
      <p className="font-semibold text-gray-700 mb-1">{label}</p>
      {payload.map((p: any) => (
        <div key={p.dataKey} className="flex items-center gap-2">
          <div className="w-2 h-2 rounded-full" style={{ background: p.color }} />
          <span className="text-gray-600">{p.name}: <b>{fmt(p.value)}</b></span>
        </div>
      ))}
    </div>
  );
};

function ProyeccionSeguro() {
  const [input, setInput] = useState<SimInput>({
    tipoSeguro: 'Vida', aseguradora: 'GNP', nombreCliente: '',
    edadAsegurado: 35, primaMensual: 1500, sumaAsegurada: 1000000,
    plazo: 20, tasaAnual: 8,
  });
  const [rows, setRows] = useState<YearRow[] | null>(null);
  const [showEmail, setShowEmail] = useState(false);
  const [email, setEmail] = useState('');

  const set = (k: keyof SimInput, v: any) => setInput(p => ({ ...p, [k]: v }));
  const last = rows?.[rows.length - 1];

  const handleEmail = () => {
    if (!rows || !last) return;
    const roi = Math.round((last.valorRescate / last.totalPagado - 1) * 100);
    const subject = `Proyección ${input.tipoSeguro} — ${input.nombreCliente || 'Prospecto'}`;
    const body = [
      `Estimado${input.nombreCliente ? ' ' + input.nombreCliente : ' cliente'},`,
      '', `Plan de ${input.tipoSeguro} — ${input.aseguradora}`,
      `Prima mensual: ${fmt(input.primaMensual)}`,
      `Suma asegurada: ${fmt(input.sumaAsegurada)}`,
      `Plazo: ${input.plazo} años · Tasa proyectada: ${input.tasaAnual}%`,
      '', `RESULTADOS A ${input.plazo} AÑOS:`,
      `• Total invertido: ${fmt(last.totalPagado)}`,
      `• Valor acumulado: ${fmt(last.valorRescate)}`,
      `• Intereses generados: ${fmt(last.intereses)}`,
      `• ROI: ${roi}%`, `• Cobertura: ${fmt(input.sumaAsegurada)}`,
      '', 'Atentamente,', 'Seguros Monterrey — SMNY',
    ].join('\n');
    window.open(`mailto:${email}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`);
    setShowEmail(false);
  };

  return (
    <div className="space-y-6">
      {/* Input card */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 print:hidden">
        <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-5">Parámetros del seguro</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {[
            { label: 'Tipo de seguro', node: (
              <select value={input.tipoSeguro} onChange={e => set('tipoSeguro', e.target.value)}
                className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-300">
                {['Vida','Dotal','Retiro','Educativo','Inversión'].map(t => <option key={t}>{t}</option>)}
              </select>
            )},
            { label: 'Aseguradora', node: (
              <select value={input.aseguradora} onChange={e => set('aseguradora', e.target.value)}
                className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-300">
                {['GNP','AXA','Metlife','SMNY','Allianz','HDI','Zurich','Inbursa','Qualitas','Mapfre'].map(a => <option key={a}>{a}</option>)}
              </select>
            )},
            { label: 'Nombre del cliente', node: (
              <input value={input.nombreCliente} onChange={e => set('nombreCliente', e.target.value)}
                placeholder="Opcional"
                className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-300" />
            )},
            { label: 'Edad del asegurado', node: (
              <input type="number" min={18} max={75} value={input.edadAsegurado}
                onChange={e => set('edadAsegurado', Number(e.target.value))}
                className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-300" />
            )},
            { label: 'Prima mensual (MXN)', node: (
              <input type="number" min={100} step={100} value={input.primaMensual}
                onChange={e => set('primaMensual', Number(e.target.value))}
                className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-300" />
            )},
            { label: 'Suma asegurada (MXN)', node: (
              <input type="number" min={50000} step={50000} value={input.sumaAsegurada}
                onChange={e => set('sumaAsegurada', Number(e.target.value))}
                className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-300" />
            )},
            { label: 'Plazo (años)', node: (
              <select value={input.plazo} onChange={e => set('plazo', Number(e.target.value))}
                className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-300">
                {[5,10,15,20,25,30].map(p => <option key={p} value={p}>{p} años</option>)}
              </select>
            )},
            { label: 'Rendimiento anual proyectado (%)', node: (
              <input type="number" min={0} max={20} step={0.5} value={input.tasaAnual}
                onChange={e => set('tasaAnual', Number(e.target.value))}
                className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-300" />
            )},
          ].map(({ label, node }) => (
            <div key={label}>
              <label className="text-xs text-gray-500 mb-1 block">{label}</label>
              {node}
            </div>
          ))}
        </div>
        <div className="mt-5 flex justify-end">
          <button onClick={() => setRows(calcularProyeccion(input))}
            className="bg-primary-600 text-white px-6 py-2.5 rounded-xl text-sm font-semibold hover:bg-primary-700 transition-colors shadow-sm">
            Calcular proyección
          </button>
        </div>
      </div>

      {rows && last && (
        <div id="simulator-results">
          {/* Print header */}
          <div className="hidden print:block mb-6 border-b pb-4">
            <h1 className="text-2xl font-bold text-gray-900">Proyección de Seguro — {input.tipoSeguro}</h1>
            <p className="text-gray-600">{input.aseguradora} · {input.nombreCliente || 'Prospecto'} · {input.edadAsegurado} años</p>
            <p className="text-gray-400 text-sm">Generado: {new Date().toLocaleDateString('es-MX', { dateStyle: 'long' })}</p>
          </div>

          {/* Summary cards */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {[
              { label: 'Total invertido',   value: fmt(last.totalPagado),    color: '#1d3d6b', icon: '💵', sub: `${input.plazo} años × ${fmt(input.primaMensual)}/mes` },
              { label: 'Valor acumulado',   value: fmt(last.valorRescate),   color: '#6b9b65', icon: '📈', sub: 'al vencimiento' },
              { label: 'Intereses ganados', value: fmt(last.intereses),      color: '#f59e0b', icon: '✨', sub: `ROI ${Math.round((last.valorRescate / last.totalPagado - 1) * 100)}%` },
              { label: 'Suma asegurada',    value: fmt(input.sumaAsegurada), color: '#1d3d6b', icon: '🛡️', sub: 'cobertura por fallecimiento' },
            ].map(c => (
              <div key={c.label} className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5">
                <div className="flex justify-between items-start mb-2">
                  <p className="text-xs text-gray-500 uppercase tracking-wide font-semibold leading-tight">{c.label}</p>
                  <span className="text-xl">{c.icon}</span>
                </div>
                <p className="text-xl font-bold mt-1" style={{ color: c.color }}>{c.value}</p>
                <p className="text-xs text-gray-400 mt-1">{c.sub}</p>
              </div>
            ))}
          </div>

          {/* Chart */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 mt-4">
            <h3 className="text-sm font-semibold text-gray-700 mb-5">Proyección año por año</h3>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={rows} margin={{ top: 5, right: 20, bottom: 5, left: 10 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" vertical={false} />
                <XAxis dataKey="anio" tick={{ fontSize: 11, fill: '#9ca3af' }} tickFormatter={v => `Año ${v}`} axisLine={false} tickLine={false} />
                <YAxis tick={{ fontSize: 11, fill: '#9ca3af' }} axisLine={false} tickLine={false}
                  tickFormatter={v => v >= 1000000 ? `$${(v/1000000).toFixed(1)}M` : `$${(v/1000).toFixed(0)}k`} />
                <Tooltip content={<ChartTooltip />} />
                <Legend wrapperStyle={{ fontSize: 11, paddingTop: 16 }} />
                <Line dataKey="totalPagado"   name="Total Pagado"    stroke="#94a3b8" strokeWidth={2} dot={false} />
                <Line dataKey="valorRescate"  name="Valor Acumulado" stroke="#6b9b65" strokeWidth={2.5} dot={false} />
                <Line dataKey="sumaAsegurada" name="Suma Asegurada"  stroke="#1d3d6b" strokeWidth={2} strokeDasharray="6 3" dot={false} />
              </LineChart>
            </ResponsiveContainer>
          </div>

          {/* Table */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 mt-4">
            <h3 className="text-sm font-semibold text-gray-700 mb-4">Tabla detallada</h3>
            <div className="overflow-x-auto">
              <table className="w-full text-sm min-w-[560px]">
                <thead>
                  <tr className="border-b border-gray-100">
                    {['Año','Edad','Total Pagado','Valor Rescate','Intereses','Suma Asegurada'].map((h, i) => (
                      <th key={h} className={`text-xs text-gray-500 uppercase pb-3 font-semibold ${i < 2 ? 'text-left pr-4' : 'text-right pr-4 last:pr-0'}`}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r, i) => (
                    <tr key={r.anio} className={`border-b border-gray-50 ${i % 2 !== 0 ? 'bg-gray-50/40' : ''}`}>
                      <td className="py-2 pr-4 font-semibold text-gray-700">{r.anio}</td>
                      <td className="py-2 pr-4 text-gray-500">{r.edad}</td>
                      <td className="py-2 pr-4 text-right text-gray-600">{fmt(r.totalPagado)}</td>
                      <td className="py-2 pr-4 text-right font-semibold text-accent-600">{fmt(r.valorRescate)}</td>
                      <td className="py-2 pr-4 text-right text-amber-600">{fmt(r.intereses)}</td>
                      <td className="py-2 text-right text-primary-600 font-semibold">{fmt(r.sumaAsegurada)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <p className="text-xs text-gray-400 mt-3 leading-relaxed">
            * Proyección ilustrativa. Los valores reales dependen del producto y condiciones de la aseguradora.
          </p>

          {/* Actions */}
          <div className="flex flex-wrap gap-3 justify-end mt-4 print:hidden">
            <button onClick={() => setShowEmail(true)}
              className="flex items-center gap-2 border border-gray-200 bg-white text-gray-700 px-4 py-2 rounded-xl text-sm hover:bg-gray-50 shadow-sm">
              ✉️ Enviar por correo
            </button>
            <button onClick={() => window.print()}
              className="flex items-center gap-2 bg-primary-600 text-white px-4 py-2 rounded-xl text-sm hover:bg-primary-700 shadow-sm">
              ⬇️ Descargar PDF
            </button>
          </div>
        </div>
      )}

      {/* Email modal */}
      {showEmail && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-xl p-6 w-full max-w-sm">
            <h3 className="font-semibold text-gray-800 mb-1">Enviar proyección por correo</h3>
            <p className="text-xs text-gray-500 mb-4">Se abrirá tu cliente de correo con la proyección lista para enviar.</p>
            <input type="email" value={email} onChange={e => setEmail(e.target.value)}
              placeholder="correo@ejemplo.com"
              className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm mb-4 focus:outline-none focus:ring-2 focus:ring-primary-300" />
            <div className="flex gap-2 justify-end">
              <button onClick={() => setShowEmail(false)}
                className="px-4 py-2 border rounded-xl text-sm text-gray-600 hover:bg-gray-50">Cancelar</button>
              <button onClick={handleEmail} disabled={!email}
                className="px-4 py-2 bg-primary-600 text-white rounded-xl text-sm hover:bg-primary-700 disabled:opacity-50">
                Abrir correo
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* ════════════════════════════════════════════════════════════════
   SECTION 2 — PPR TAX REFUND CALCULATOR
   ════════════════════════════════════════════════════════════════ */

// ISR annual tariff table 2025 (LISR Art. 152)
const ISR_TABLE = [
  { li: 0.01,        cuota: 0,           tasa: 0.0192 },
  { li: 8952.50,     cuota: 171.88,      tasa: 0.0640 },
  { li: 75984.56,    cuota: 4472.38,     tasa: 0.1088 },
  { li: 133536.08,   cuota: 10733.22,    tasa: 0.1600 },
  { li: 155229.81,   cuota: 14211.82,    tasa: 0.1792 },
  { li: 185852.58,   cuota: 19697.24,    tasa: 0.2136 },
  { li: 374837.89,   cuota: 60082.10,    tasa: 0.2352 },
  { li: 590796.00,   cuota: 110889.49,   tasa: 0.3000 },
  { li: 1127926.85,  cuota: 271981.99,   tasa: 0.3200 },
  { li: 1503902.47,  cuota: 392294.17,   tasa: 0.3400 },
  { li: 4511707.38,  cuota: 1417175.23,  tasa: 0.3500 },
];

const UMA_ANUAL_2025 = 39628.05; // $108.57/día × 365

function calcISR(ingreso: number): number {
  if (ingreso <= 0) return 0;
  const bracket = [...ISR_TABLE].reverse().find(b => ingreso >= b.li);
  if (!bracket) return 0;
  return bracket.cuota + (ingreso - bracket.li) * bracket.tasa;
}

function tasaMarginal(ingreso: number): number {
  if (ingreso <= 0) return 0;
  return ([...ISR_TABLE].reverse().find(b => ingreso >= b.li)?.tasa ?? 0) * 100;
}

interface PPRInput {
  ingresoMensual: number;
  aportacionMensual: number;
  regimen: 'Asalariado' | 'Honorarios / RIF';
}

interface PPRResult {
  ingresoAnual: number;
  aportacionAnual: number;
  limiteDeduccion: number;
  deduccionEfectiva: number;
  isrSinPPR: number;
  isrConPPR: number;
  ahorroFiscal: number;
  tasaMarginal: number;
  costoNetoMensual: number;
  porcentajeAhorro: number;
}

function calcPPR(input: PPRInput): PPRResult {
  const ingresoAnual = input.ingresoMensual * 12;
  const aportacionAnual = input.aportacionMensual * 12;
  const limiteDeduccion = Math.min(ingresoAnual * 0.10, 5 * UMA_ANUAL_2025);
  const deduccionEfectiva = Math.min(aportacionAnual, limiteDeduccion);
  const isrSinPPR = calcISR(ingresoAnual);
  const isrConPPR = calcISR(ingresoAnual - deduccionEfectiva);
  const ahorroFiscal = Math.max(0, isrSinPPR - isrConPPR);
  const costoNetoMensual = Math.max(0, aportacionAnual - ahorroFiscal) / 12;
  const porcentajeAhorro = aportacionAnual > 0 ? (ahorroFiscal / aportacionAnual) * 100 : 0;
  return {
    ingresoAnual, aportacionAnual, limiteDeduccion, deduccionEfectiva,
    isrSinPPR, isrConPPR, ahorroFiscal, costoNetoMensual,
    tasaMarginal: tasaMarginal(ingresoAnual),
    porcentajeAhorro,
  };
}

function CalculadoraPPR() {
  const [input, setInput] = useState<PPRInput>({
    ingresoMensual: 30000,
    aportacionMensual: 2000,
    regimen: 'Asalariado',
  });
  const [result, setResult] = useState<PPRResult | null>(null);
  const [showEmail, setShowEmail] = useState(false);
  const [email, setEmail] = useState('');

  const set = (k: keyof PPRInput, v: any) => setInput(p => ({ ...p, [k]: v }));

  const handleCalcular = () => setResult(calcPPR(input));

  const handleEmail = () => {
    if (!result) return;
    const subject = 'Beneficio Fiscal PPR — Devolución ISR estimada';
    const body = [
      'Estimado cliente,',
      '',
      'Le comparto el análisis de su beneficio fiscal con un Plan Personal de Retiro (PPR):',
      '',
      `Ingreso mensual: ${fmt(input.ingresoMensual)}`,
      `Aportación PPR mensual: ${fmt(input.aportacionMensual)}`,
      `Régimen fiscal: ${input.regimen}`,
      '',
      'RESULTADO FISCAL:',
      `• Deducción máxima permitida: ${fmt(result.limiteDeduccion)}`,
      `• Deducción efectiva aplicada: ${fmt(result.deduccionEfectiva)}`,
      `• ISR sin PPR: ${fmt(result.isrSinPPR)}`,
      `• ISR con PPR: ${fmt(result.isrConPPR)}`,
      `• Ahorro fiscal anual estimado: ${fmt(result.ahorroFiscal)}`,
      `• Costo neto mensual (después del beneficio): ${fmt(result.costoNetoMensual)}`,
      `• Tasa marginal: ${result.tasaMarginal.toFixed(2)}%`,
      '',
      '* Cifras estimadas con tarifa ISR 2025. Consulte a su contador.',
      '',
      'Atentamente,',
      'Seguros Monterrey — SMNY',
    ].join('\n');
    window.open(`mailto:${email}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`);
    setShowEmail(false);
  };

  return (
    <div className="space-y-6">
      {/* Info banner */}
      <div className="bg-primary-50 border border-primary-100 rounded-2xl p-4 flex gap-3">
        <span className="text-2xl flex-shrink-0">📋</span>
        <div>
          <p className="text-sm font-semibold text-primary-800">¿Qué es el PPR?</p>
          <p className="text-xs text-primary-600 mt-0.5 leading-relaxed">
            El <b>Plan Personal de Retiro</b> te permite deducir tus aportaciones del ISR anual (Art. 151, Frac. V, LISR).
            El límite de deducción es el menor entre el 10% de tu ingreso anual o 5 UMAs anuales
            ({fmt(5 * UMA_ANUAL_2025)} para 2025).
          </p>
        </div>
      </div>

      {/* Input card */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 print:hidden">
        <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-5">Datos fiscales</h3>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div>
            <label className="text-xs text-gray-500 mb-1 block">Régimen fiscal</label>
            <select value={input.regimen} onChange={e => set('regimen', e.target.value as any)}
              className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-300">
              <option>Asalariado</option>
              <option>Honorarios / RIF</option>
            </select>
          </div>
          <div>
            <label className="text-xs text-gray-500 mb-1 block">Ingreso mensual bruto (MXN)</label>
            <input type="number" min={1000} step={500} value={input.ingresoMensual}
              onChange={e => set('ingresoMensual', Number(e.target.value))}
              className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-300" />
          </div>
          <div>
            <label className="text-xs text-gray-500 mb-1 block">Aportación PPR mensual (MXN)</label>
            <input type="number" min={100} step={100} value={input.aportacionMensual}
              onChange={e => set('aportacionMensual', Number(e.target.value))}
              className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-300" />
          </div>
        </div>
        <div className="mt-5 flex justify-end">
          <button onClick={handleCalcular}
            className="bg-primary-600 text-white px-6 py-2.5 rounded-xl text-sm font-semibold hover:bg-primary-700 transition-colors shadow-sm">
            Calcular devolución
          </button>
        </div>
      </div>

      {/* Results */}
      {result && (
        <div id="ppr-results">
          {/* Print header */}
          <div className="hidden print:block mb-6 border-b pb-4">
            <h1 className="text-2xl font-bold text-gray-900">Beneficio Fiscal PPR — Life & Legacy CRM</h1>
            <p className="text-gray-400 text-sm">Generado: {new Date().toLocaleDateString('es-MX', { dateStyle: 'long' })}</p>
          </div>

          {/* Key metric highlight */}
          <div className="bg-accent-50 border-2 border-accent-200 rounded-2xl p-6 flex flex-col sm:flex-row items-center gap-4">
            <div className="text-5xl">💰</div>
            <div className="flex-1 text-center sm:text-left">
              <p className="text-sm text-accent-700 font-semibold uppercase tracking-wide">Ahorro fiscal estimado anual</p>
              <p className="text-4xl font-bold text-accent-600 mt-1">{fmt(result.ahorroFiscal)}</p>
              <p className="text-sm text-accent-600 mt-1">
                ≈ {fmt(result.ahorroFiscal / 12)}/mes · recuperas el <b>{result.porcentajeAhorro.toFixed(0)}%</b> de tu aportación vía devolución de ISR
              </p>
            </div>
            <div className="text-center bg-white rounded-xl px-5 py-3 shadow-sm border border-accent-100">
              <p className="text-xs text-gray-500 uppercase font-semibold">Costo neto mensual</p>
              <p className="text-2xl font-bold text-primary-700 mt-0.5">{fmt(result.costoNetoMensual)}</p>
              <p className="text-xs text-gray-400">en lugar de {fmt(input.aportacionMensual)}</p>
            </div>
          </div>

          {/* Detail cards */}
          <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 mt-4">
            {[
              { label: 'Ingreso anual',           value: fmt(result.ingresoAnual),         icon: '📅', color: '#1d3d6b' },
              { label: 'Aportación PPR anual',    value: fmt(result.aportacionAnual),      icon: '💼', color: '#1d3d6b' },
              { label: 'Límite de deducción',     value: fmt(result.limiteDeduccion),      icon: '📏', color: '#6b9b65',
                sub: result.aportacionAnual > result.limiteDeduccion ? '⚠️ Aportación excede el límite' : '✅ Dentro del límite' },
              { label: 'ISR sin PPR',             value: fmt(result.isrSinPPR),            icon: '📤', color: '#ef4444' },
              { label: 'ISR con PPR',             value: fmt(result.isrConPPR),            icon: '📥', color: '#6b9b65' },
              { label: 'Tasa marginal aplicable', value: `${result.tasaMarginal.toFixed(2)}%`, icon: '📊', color: '#f59e0b',
                sub: 'sobre el último peso de ingreso' },
            ].map(c => (
              <div key={c.label} className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5">
                <div className="flex justify-between items-start mb-2">
                  <p className="text-xs text-gray-500 uppercase tracking-wide font-semibold leading-tight">{c.label}</p>
                  <span className="text-lg">{c.icon}</span>
                </div>
                <p className="text-xl font-bold" style={{ color: c.color }}>{c.value}</p>
                {c.sub && <p className="text-xs text-gray-400 mt-1">{c.sub}</p>}
              </div>
            ))}
          </div>

          {/* Chart — ISR comparison */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 mt-4">
            <h3 className="text-sm font-semibold text-gray-700 mb-5">Comparativa de ISR anual</h3>
            <ResponsiveContainer width="100%" height={220}>
              <BarChart
                data={[
                  { name: 'Sin PPR', isr: Math.round(result.isrSinPPR), neto: Math.round(result.ingresoAnual - result.isrSinPPR) },
                  { name: 'Con PPR', isr: Math.round(result.isrConPPR), neto: Math.round(result.ingresoAnual - result.isrConPPR) },
                ]}
                margin={{ top: 0, right: 0, bottom: 0, left: 20 }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" vertical={false} />
                <XAxis dataKey="name" tick={{ fontSize: 12, fill: '#6b7280' }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fontSize: 11, fill: '#9ca3af' }} axisLine={false} tickLine={false}
                  tickFormatter={v => `$${(v/1000).toFixed(0)}k`} />
                <Tooltip formatter={(v: any) => fmt(v)} />
                <Legend wrapperStyle={{ fontSize: 11, paddingTop: 12 }} />
                <Bar dataKey="isr"  name="ISR a pagar"   radius={[6,6,0,0]} maxBarSize={80}>
                  <Cell fill="#ef4444" />
                  <Cell fill="#6b9b65" />
                </Bar>
              </BarChart>
            </ResponsiveContainer>
            <div className="flex items-center justify-center gap-6 mt-2">
              <div className="flex items-center gap-2 text-xs text-gray-500">
                <div className="w-3 h-3 rounded bg-red-400" /> Sin PPR: {fmt(result.isrSinPPR)}
              </div>
              <div className="flex items-center gap-2 text-xs text-gray-500">
                <div className="w-3 h-3 rounded bg-accent-500" /> Con PPR: {fmt(result.isrConPPR)}
              </div>
              <div className="flex items-center gap-2 text-xs font-semibold text-accent-600">
                Ahorro: {fmt(result.ahorroFiscal)}
              </div>
            </div>
          </div>

          {/* How it works */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 mt-4">
            <h3 className="text-sm font-semibold text-gray-700 mb-4">¿Cómo funciona la devolución?</h3>
            <div className="space-y-3">
              {[
                { paso: '1', texto: `Aportas ${fmt(input.aportacionMensual)}/mes a tu PPR durante el año`, color: 'bg-primary-100 text-primary-700' },
                { paso: '2', texto: `En tu declaración anual deduces ${fmt(result.deduccionEfectiva)} de tu base gravable`, color: 'bg-primary-100 text-primary-700' },
                { paso: '3', texto: `Tu ISR se reduce de ${fmt(result.isrSinPPR)} a ${fmt(result.isrConPPR)}`, color: 'bg-amber-100 text-amber-700' },
                { paso: '4', texto: `El SAT te devuelve ${fmt(result.ahorroFiscal)} — tu costo real es ${fmt(result.costoNetoMensual)}/mes`, color: 'bg-accent-100 text-accent-700' },
              ].map(s => (
                <div key={s.paso} className="flex items-center gap-3">
                  <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0 ${s.color}`}>
                    {s.paso}
                  </div>
                  <p className="text-sm text-gray-600">{s.texto}</p>
                </div>
              ))}
            </div>
          </div>

          <p className="text-xs text-gray-400 mt-3 leading-relaxed">
            * Cálculo estimado con tarifa ISR anual 2025 (Art. 152 LISR) y UMA {fmt(UMA_ANUAL_2025)}/año.
            Las cifras reales pueden variar según tu situación fiscal. Consulta a tu contador.
          </p>

          {/* Actions */}
          <div className="flex flex-wrap gap-3 justify-end mt-4 print:hidden">
            <button onClick={() => setShowEmail(true)}
              className="flex items-center gap-2 border border-gray-200 bg-white text-gray-700 px-4 py-2 rounded-xl text-sm hover:bg-gray-50 shadow-sm">
              ✉️ Enviar por correo
            </button>
            <button onClick={() => window.print()}
              className="flex items-center gap-2 bg-primary-600 text-white px-4 py-2 rounded-xl text-sm hover:bg-primary-700 shadow-sm">
              ⬇️ Descargar PDF
            </button>
          </div>
        </div>
      )}

      {/* Email modal */}
      {showEmail && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-xl p-6 w-full max-w-sm">
            <h3 className="font-semibold text-gray-800 mb-1">Enviar análisis PPR por correo</h3>
            <p className="text-xs text-gray-500 mb-4">Se abrirá tu cliente de correo con el resumen fiscal listo para enviar.</p>
            <input type="email" value={email} onChange={e => setEmail(e.target.value)}
              placeholder="correo@ejemplo.com"
              className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm mb-4 focus:outline-none focus:ring-2 focus:ring-primary-300" />
            <div className="flex gap-2 justify-end">
              <button onClick={() => setShowEmail(false)}
                className="px-4 py-2 border rounded-xl text-sm text-gray-600 hover:bg-gray-50">Cancelar</button>
              <button onClick={handleEmail} disabled={!email}
                className="px-4 py-2 bg-primary-600 text-white rounded-xl text-sm hover:bg-primary-700 disabled:opacity-50">
                Abrir correo
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* ════════════════════════════════════════════════════════════════
   MAIN — Tab switcher
   ════════════════════════════════════════════════════════════════ */
const TABS = [
  { id: 'proyeccion', label: 'Proyección de Seguro', icon: '📈' },
  { id: 'ppr',        label: 'PPR — Devolución Fiscal', icon: '🏛️' },
];

export default function Simulador() {
  const [tab, setTab] = useState<'proyeccion' | 'ppr'>('proyeccion');

  return (
    <div className="space-y-6">
      {/* Tab bar */}
      <div className="flex gap-2 bg-white rounded-2xl shadow-sm border border-gray-100 p-1.5 w-fit">
        {TABS.map(t => (
          <button
            key={t.id}
            onClick={() => setTab(t.id as any)}
            className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium transition-all ${
              tab === t.id
                ? 'bg-primary-600 text-white shadow-sm'
                : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
            }`}
          >
            <span>{t.icon}</span>
            <span className="hidden sm:inline">{t.label}</span>
          </button>
        ))}
      </div>

      {tab === 'proyeccion' ? <ProyeccionSeguro /> : <CalculadoraPPR />}
    </div>
  );
}
